import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PhoneInput from 'react-phone-input-2';
import { Eye, EyeOff, ArrowLeft } from 'lucide-react';

import 'react-phone-input-2/lib/style.css';
import AuthLayout from '../../shared/components/AuthLayout';
import Button from '../../shared/components/Button';
import Input from '../../shared/components/Input';
import OverlayLoader from '../../components/Loader/OverlayLoader';
import loginImage from '../../assets/images/login.png';
import { useAuth } from '../../shared/hooks/useAuth';
import { AUTH_STACK } from '../../utils/screenNames';
import { loginWithEmailPhone } from '../../redux/actions/authActions';

/**
 * LoginWithMobile Page - Refactored with Tailwind CSS
 * Mobile phone number login
 */
function LoginWithMobile() {
  const navigate = useNavigate();
  const { handleAuthSuccess } = useAuth();

  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [telephone, setTelephone] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  /**
   * Handle mobile login
   */
  const handleLogin = async () => {
    if (!telephone) {
      setError('Please enter your phone number');
      return;
    }

    if (!password) {
      setError('Please enter password');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      await loginWithEmailPhone(
        null,
        `+${telephone}`,
        password,
        (userData) => {
          handleAuthSuccess(userData, undefined, { loginMethod: 'phone', isSignup: false });
        },
        (errMsg) => {
          setError(errMsg || 'Login failed');
          setIsLoading(false);
        }
      );
    } catch (error) {
      setError('An error occurred. Please try again.');
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return <OverlayLoader />;
  }

  return (
    <div className="relative w-screen h-screen bg-bg-dark">
      {/* Back Button */}
      <button
        onClick={() => navigate(-1)}
        className="absolute top-6 left-6 z-20 flex items-center gap-2 text-shaded-text hover:text-[#FEFCEB] transition-colors"
      >
        <ArrowLeft className="w-5 h-5" />
        <span className="font-proxima text-sm">Back</span>
      </button>

      {/* Main Content */}
      <AuthLayout
        heroImage={loginImage}
        title="Log in with mobile"
        subtitle="Enter your phone number and password"
      >
        {/* Phone Input */}
        <div className="w-full">
          <label className="block text-[#FEFCEB] text-sm font-proxima mb-2">
            Phone Number
          </label>
          <PhoneInput
            country={'gb'}
            value={telephone}
            onChange={(phone) => setTelephone(phone)}
            containerClass="w-full"
            inputClass="!w-full !h-12 !bg-blue-shade-8 !text-[#FEFCEB] !border-blue-shade-7 !rounded-clubbie !font-proxima"
            buttonClass="!bg-blue-shade-7 !border-blue-shade-7 !rounded-l-clubbie"
            dropdownClass="!bg-blue-shade-8 !text-[#FEFCEB]"
          />
        </div>

        {/* Password Input */}
        <div className="w-full mt-4">
          <label className="block text-[#FEFCEB] text-sm font-proxima mb-2">
            Password
          </label>
          <Input
            type={showPassword ? 'text' : 'password'}
            name="password"
            placeholder="Password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              setError('');
            }}
            error={!!error}
            errorMessage={error}
            rightIcon={
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="text-shaded-text hover:text-[#FEFCEB] transition-colors"
              >
                {showPassword ? (
                  <EyeOff className="w-5 h-5" />
                ) : (
                  <Eye className="w-5 h-5" />
                )}
              </button>
            }
          />
        </div>

        {/* Forgot Password Link */}
        <button
          onClick={() => navigate(AUTH_STACK.FORGOT_PASSWORD_WITH_MOBILE)}
          className="w-full text-left text-sm text-shaded-text hover:text-light-blue transition-colors font-proxima mt-2"
        >
          Forgot password?
        </button>

        {/* Login Button */}
        <Button
          variant="primary"
          size="lg"
          onClick={handleLogin}
          className="w-full mt-6"
          disabled={!telephone || !password}
        >
          Log in
        </Button>
      </AuthLayout>
    </div>
  );
}

export default LoginWithMobile;
